import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type {
  LineStringObject,
  PointObject,
  PointOnLineStringObject,
} from '@racemap/utilities/types/geos';
import type { MapMouseEvent } from 'mapbox-gl';
import { type FC, useCallback, useContext, useMemo, useRef } from 'react';
import { Layer } from 'react-map-gl/mapbox';
import { MapContext } from '../BasicMap/BasicMap';
import { PointLayer } from './PointLayer';

interface Props {
  points: Array<PointOnLineStringObject>;
  lineStrings: Array<LineStringObject>;
  selectedFeatureIds?: Array<string>;
  onClick?: (
    point: PointOnLineStringObject,
    lineString: LineStringObject,
    evt: MapMouseEvent,
  ) => void;
}

export const PointsOnLineStringsLayer: FC<Props> = ({
  points,
  lineStrings,
  selectedFeatureIds,
  onClick,
}) => {
  const { current: id } = useRef(`point-on-linestring-${shortIdBuilder()}`);
  const ctx = useContext(MapContext);
  const mapStyle = ctx?.mapStyle;

  const selectedIds = useMemo(() => new Set(selectedFeatureIds), [selectedFeatureIds]);
  const lineStringsMap = useMemo(() => new Map(lineStrings.map((l) => [l.id, l])), [lineStrings]);

  const pointsUnselected = useMemo(
    () => getPointsOfUnselectedLineStrings(points, lineStringsMap, selectedIds),
    [points, lineStringsMap, selectedIds],
  );
  const selectedPoints = useMemo(
    () => points.filter((p) => selectedIds.has(p.id)),
    [points, selectedIds],
  );
  const lineStringIndexMap = useMemo(
    () => getLineStringIndexMap(points, lineStringsMap, selectedIds),
    [points, lineStringsMap, selectedIds],
  );
  const handleClick = useCallback(
    (evt: MapMouseEvent) => {
      if (onClick) {
        const { features } = evt;
        const firstFeature = features?.[0];
        if (firstFeature == null) return;

        const point = points.find((p) => p.id === firstFeature.id);
        if (point == null) return;

        const lineString = lineStringsMap.get(point.properties.lineStringId);
        if (lineString == null) return;

        onClick(point, lineString, evt);
      }
    },
    [onClick, points, lineStringsMap],
  );
  const filter = getFilter(lineStringIndexMap);
  const sources = mapStyle ? Object.keys(mapStyle.sources) : [];
  const hasColdSource = sources.includes('mapbox-gl-draw-cold');
  const hasHotSource = sources.includes('mapbox-gl-draw-hot');

  return (
    <>
      <PointLayer id={id} key={id} points={pointsUnselected} onClick={handleClick} />
      <PointLayer
        id={`selected-${id}`}
        key={`selected-${id}`}
        points={selectedPoints}
        color={RacemapColors.BaseGreen}
        radius={5}
      />
      {hasColdSource && (
        <Layer
          id={`stroke-cold-${id}`}
          key={`stroke-cold-${id}`}
          type="circle"
          source="mapbox-gl-draw-cold"
          filter={filter}
          paint={{ 'circle-radius': 6, 'circle-color': RacemapColors.PaleBlue }}
        />
      )}

      {hasHotSource && (
        <Layer
          id={`stroke-hot-${id}`}
          key={`stroke-hot-${id}`}
          type="circle"
          source="mapbox-gl-draw-hot"
          filter={filter}
          paint={{ 'circle-radius': 6, 'circle-color': RacemapColors.PaleBlue }}
        />
      )}

      {hasColdSource && (
        <Layer
          id={`fill-cold-${id}`}
          key={`fill-cold-${id}`}
          type="circle"
          source="mapbox-gl-draw-cold"
          filter={filter}
          paint={{ 'circle-radius': 4, 'circle-color': 'white' }}
        />
      )}
      {hasHotSource && (
        <Layer
          id={`fill-hot-${id}`}
          key={`fill-hot-${id}`}
          type="circle"
          source="mapbox-gl-draw-hot"
          filter={filter}
          paint={{ 'circle-radius': 4, 'circle-color': 'white' }}
        />
      )}
    </>
  );
};

function getFilter(indexMap: Record<string, Array<string>>): Array<any> {
  const entries = Object.entries(indexMap);
  if (entries.length === 0) return ['!', true];

  return [
    'all',
    ['==', 'meta', 'vertex'],
    ['==', '$type', 'Point'],
    [
      'any',
      ...entries.map(([parentId, lineStringIndexes]) => [
        'all',
        ['==', 'parent', parentId],
        ['in', 'coord_path', ...lineStringIndexes],
      ]),
    ],
  ];
}

function getPointsOfUnselectedLineStrings(
  points: Array<PointOnLineStringObject>,
  lineStrings: Map<string, LineStringObject>,
  selectedIds: Set<string>,
): Array<PointObject> {
  const pointsUnselected: Array<PointObject> = [];

  for (const point of points) {
    if (selectedIds.has(point.properties.lineStringId)) continue;

    const lineString = lineStrings.get(point.properties.lineStringId);
    if (lineString == null) continue;
    if (lineString.geometry.coordinates[point.properties.coordinateIndex] == null) continue;

    pointsUnselected.push({
      ...point,
      geometry: {
        type: 'Point',
        coordinates: lineString.geometry.coordinates[point.properties.coordinateIndex],
      },
    });
  }

  return pointsUnselected;
}

function getLineStringIndexMap(
  points: Array<PointOnLineStringObject>,
  lineStrings: Map<string, LineStringObject>,
  selectedIds: Set<string>,
): Record<string, Array<string>> {
  const indexMap: Record<string, Array<string>> = {};

  for (const point of points) {
    if (!selectedIds.has(point.properties.lineStringId)) continue;

    const lineString = lineStrings.get(point.properties.lineStringId);
    if (lineString == null) continue;

    if (indexMap[lineString.id] == null) indexMap[lineString.id] = [];
    indexMap[lineString.id].push(point.properties.coordinateIndex.toString());
  }

  return indexMap;
}
